import React, { useState, useEffect, useReducer, useRef, useCallback, useContext } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import toast from "react-hot-toast";

import { isSameDay, parseISO, format, differenceInCalendarDays, startOfDay } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@mui/material/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { keyframes } from "tss-react";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  InsertDriveFile,
} from "@mui/icons-material";

import MarkdownWrapper from "../MarkdownWrapper";
import MessageText from "../MessageText";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import { i18n } from "../../translate/i18n";
import { SearchMessageContext } from "../../context/SearchMessage/SearchMessageContext";

const highlightFlash = keyframes({
  "0%":   { boxShadow: "0 0 0 3px rgba(66,144,132,0.75)" },
  "60%":  { boxShadow: "0 0 0 3px rgba(66,144,132,0.75)" },
  "100%": { boxShadow: "0 0 0 0   rgba(66,144,132,0)" },
});

const useStyles = makeStyles()((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundImage: theme.palette.mode === "dark"
      ? `url(${whatsBackgroundDark})`
      : `url(${whatsBackground})`,
    backgroundColor: theme.palette.mode === "dark" ? "#0B141A" : "#efeae2",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 6px 90px 6px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "min(600px, 85vw)",
    [theme.breakpoints.down("sm")]: {
      marginRight: 8,
      maxWidth: "88vw",
    },
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.mode === "dark" ? "#202C33" : "#ffffff",
    color: theme.palette.mode === "dark" ? "#e9edef" : "#111b21",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px rgba(0,0,0,0.13)",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    cursor: "pointer",
  },

  messageHighlight: {
    animation: `${highlightFlash} 1.8s ease-out forwards`,
  },

  quotedMsg: {
    padding: 10,
    maxWidth: "min(300px, 70vw)",
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.secondary.main,
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "min(600px, 85vw)",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 8,
      maxWidth: "88vw",
    },
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.mode === "dark" ? "#005C4B" : "#d9fdd3",
    color: theme.palette.mode === "dark" ? "#e9edef" : "#111b21",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px rgba(0,0,0,0.13)",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    cursor: "pointer",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.primary.main,
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: theme.palette.secondary.light,
    fontWeight: 500,
  },

  agentName: {
    display: "block",
    fontSize: 11.5,
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: 2,
    paddingLeft: 6,
    paddingRight: 6,
    opacity: 0.85,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.mode === "dark" ? "rgba(233,237,239,0.45)" : "rgba(17,27,33,0.4)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemMedia: {
    padding: "2px 6px 4px 6px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },

  timestampMedia: {
    fontSize: 11,
    color: theme.palette.mode === "dark" ? "rgba(233,237,239,0.6)" : "rgba(17,27,33,0.5)",
  },

  messageMedia: {
    objectFit: "cover",
    width: "min(250px, 72vw)",
    minWidth: "100%",
    display: "block",
    height: "auto",
    aspectRatio: "5 / 4",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    [theme.breakpoints.down("sm")]: {
      width: "min(250px, 82vw)",
    },
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.mode === "dark" ? "rgba(233,237,239,0.6)" : "rgba(17,27,33,0.5)",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "auto",
    padding: "0 12px",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.75)",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
    backdropFilter: "blur(4px)",
  },

  dailyTimestampText: {
    color: theme.palette.mode === "dark" ? "rgba(233,237,239,0.8)" : "rgba(17,27,33,0.65)",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  fileCard: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    gap: 10,
    minWidth: "min(220px, 70vw)",
    maxWidth: "min(320px, 80vw)",
  },

  fileCardIcon: {
    width: 44,
    height: 44,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  fileCardInfo: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  fileCardName: {
    fontSize: 13,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: 1.3,
  },

  fileCardExt: {
    fontSize: 11,
    opacity: 0.55,
    marginTop: 2,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  fileCardDownload: {
    color: "inherit",
    opacity: 0.65,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    "&:hover": { opacity: 1 },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const FILE_EXT_COLORS = {
  pdf: "#e53935",
  doc: "#1565c0", docx: "#1565c0",
  xls: "#2e7d32", xlsx: "#2e7d32", csv: "#2e7d32",
  ppt: "#e65100", pptx: "#e65100",
  zip: "#6d4c41", rar: "#6d4c41", "7z": "#6d4c41",
  txt: "#546e7a", rtf: "#546e7a",
  mp3: "#6a1b9a", ogg: "#6a1b9a", wav: "#6a1b9a",
  mp4: "#00838f", mov: "#00838f", avi: "#00838f",
};

const getFileExtColor = (filename) => {
  const ext = (filename || "").split(".").pop()?.toLowerCase();
  return FILE_EXT_COLORS[ext] || "#78909c";
};

const MEDIA_WITH_OWN_LAYOUT = ["image", "sticker", "video", "audio", "document", "application", "ptt"];

const MessagesList = ({ ticketId, isGroup }) => {
  const { classes } = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const parentRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const shouldScrollToBottom = useRef(false);

  const { jumpRequest, requestInputFocus } = useContext(SearchMessageContext) || {};
  const pendingJumpRef = useRef(null);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    pendingJumpRef.current = null;

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (currentTicketId.current === ticketId) {
            if (pageNumber === 1 && data.messages.length > 0) {
              shouldScrollToBottom.current = true;
            }
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const socket = openSocket();
    const numericTicketId = +ticketId;

    const handleConnect = () => socket.emit("joinChatBox", ticketId);

    const handleAppMessage = (data) => {
      if (data.message?.ticketId !== numericTicketId) return;

      if (data.action === "create") {
        shouldScrollToBottom.current = true;
        dispatch({ type: "ADD_MESSAGE", payload: data.message });

        // Fire a browser notification when the tab is hidden and the message is inbound
        if (
          document.hidden &&
          !data.message.fromMe &&
          Notification.permission === "granted" &&
          localStorage.getItem("notif_desktop") !== "false"
        ) {
          const contact = data.message.contact?.name ?? "New message";
          const body = data.message.body
            ? data.message.body.slice(0, 100)
            : data.message.mediaType ?? "Media";
          new Notification(contact, { body, icon: "/favicon.ico" });
        }
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    };

    if (socket.connected) handleConnect();
    socket.on("connect", handleConnect);
    socket.on("appMessage", handleAppMessage);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("appMessage", handleAppMessage);
    };
  }, [ticketId]);

  useEffect(() => {
    // While a search jump is pending, keep loading older pages until the
    // target message is in state — and don't let incoming messages yank
    // the view to the bottom.
    if (pendingJumpRef.current) {
      shouldScrollToBottom.current = false;
      const { messageId, targetPage } = pendingJumpRef.current;
      if (scrollToMessage(messageId)) {
        pendingJumpRef.current = null;
        requestInputFocus?.();
      } else if (loading) {
        // a page fetch is in flight; retry when it lands
      } else if (hasMore && pageNumber < targetPage + 3) {
        loadMore();
      } else {
        pendingJumpRef.current = null;
        toast.error(i18n.t("ticketMessagesSearch.notFound"));
      }
      return;
    }

    if (shouldScrollToBottom.current) {
      scrollToBottom();
      shouldScrollToBottom.current = false;
    }
  }, [messagesList]);

  useEffect(() => {
    if (!jumpRequest) return;
    pendingJumpRef.current = jumpRequest;

    if (scrollToMessage(jumpRequest.messageId)) {
      pendingJumpRef.current = null;
      requestInputFocus?.();
    } else if (loading) {
      // retry once the in-flight page lands (effect above)
    } else if (hasMore) {
      loadMore();
    } else {
      pendingJumpRef.current = null;
      toast.error(i18n.t("ticketMessagesSearch.notFound"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpRequest]);

  const virtualizer = useVirtualizer({
    count: messagesList.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => 80, []),
    overscan: 10,
  });

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (messagesList.length > 0) {
      virtualizer.scrollToIndex(messagesList.length - 1, { behavior: "smooth" });
    }
  };

  const scrollToMessage = (messageId) => {
    const index = messagesList.findIndex((m) => m.id === messageId);
    if (index === -1) return false;

    virtualizer.scrollToIndex(index, { align: "center" });

    // The row may not be in the DOM until the virtualizer renders it, and
    // dynamic measurement can shift it after the first scroll — so re-center
    // and highlight once the element exists.
    let attempts = 0;
    const tryHighlight = () => {
      const el = document.getElementById(`msg-${messageId}`);
      if (el) {
        virtualizer.scrollToIndex(index, { align: "center" });
        el.classList.add(classes.messageHighlight);
        setTimeout(() => el.classList.remove(classes.messageHighlight), 1800);
      } else if (attempts++ < 10) {
        requestAnimationFrame(tryHighlight);
      }
    };
    requestAnimationFrame(tryHighlight);

    return true;
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (message.mediaType === "image" || message.mediaType === "sticker") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio" || message.mediaType === "ptt") {
      return <Audio url={message.mediaUrl} />;
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else if (message.mediaType === "document" || message.mediaType === "application") {
      const filename = message.mediaUrl
        ? decodeURIComponent(message.mediaUrl.split("/").pop() || "file")
        : "file";
      const ext = filename.split(".").pop()?.toUpperCase() || "FILE";
      const iconColor = getFileExtColor(filename);
      return (
        <div className={classes.fileCard}>
          <div className={classes.fileCardIcon} style={{ backgroundColor: iconColor }}>
            <InsertDriveFile style={{ color: "#fff", fontSize: 24 }} />
          </div>
          <div className={classes.fileCardInfo}>
            <span className={classes.fileCardName}>{filename}</span>
            <span className={classes.fileCardExt}>{ext}</span>
          </div>
          <a
            href={message.mediaUrl}
            target="_blank"
            rel="noreferrer"
            className={classes.fileCardDownload}
          >
            <GetApp />
          </a>
        </div>
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
            >
              Download
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const formatDayLabel = (date) => {
    const diff = differenceInCalendarDays(startOfDay(new Date()), startOfDay(date));
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff <= 7) return format(date, "EEEE");
    return format(date, "M/d/yyyy");
  };

  const renderDailyTimestamps = (message, index) => {
    const msgDate = parseISO(messagesList[index].createdAt);

    if (index === 0) {
      return (
        <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
          <div className={classes.dailyTimestampText}>
            {formatDayLabel(msgDate)}
          </div>
        </span>
      );
    }

    if (index > 0) {
      const previousMsgDate = parseISO(messagesList[index - 1].createdAt);
      if (!isSameDay(msgDate, previousMsgDate)) {
        return (
          <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
            <div className={classes.dailyTimestampText}>
              {formatDayLabel(msgDate)}
            </div>
          </span>
        );
      }
    }
  };

  const shouldShowAgentName = (message) => {
    return message.fromMe && !!message.user?.name;
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const handleScrollToQuotedMsg = (quotedMsgId) => {
    const el = document.getElementById(`msg-${quotedMsgId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add(classes.messageHighlight);
    setTimeout(() => el.classList.remove(classes.messageHighlight), 1800);
  };

  const renderQuotedMessage = (message) => {
    const quotedFromMe = message.quotedMsg?.fromMe;
    const senderLabel = quotedFromMe
      ? "You"
      : message.quotedMsg?.contact?.name ?? "";

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
        onClick={() => handleScrollToQuotedMsg(message.quotedMsg?.id)}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: quotedFromMe,
          })}
        />
        <div className={message.fromMe ? classes.quotedMsgRight : classes.quotedMsg}>
          <span className={classes.messageContactName}>{senderLabel}</span>
          <div>{message.quotedMsg?.body}</div>
        </div>
      </div>
    );
  };

  const renderSingleMessage = (message, index) => {
    if (!message.fromMe) {
      return (
        <React.Fragment key={message.id}>
          {renderDailyTimestamps(message, index)}
          {renderMessageDivider(message, index)}
          <div className={classes.messageLeft} id={`msg-${message.id}`}>
            <IconButton
              variant="contained"
              size="small"
              id="messageActionsButton"
              disabled={message.isDeleted}
              className={classes.messageActionsButton}
              onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
            >
              <ExpandMore />
            </IconButton>
            {isGroup && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
              //|| message.mediaType === "multi_vcard"
            ) && checkMessageMedia(message)}
            {MEDIA_WITH_OWN_LAYOUT.includes(message.mediaType) && message.mediaUrl ? (
              message.body && ["image", "video", "document", "application"].includes(message.mediaType) ? (
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MessageText>{message.body}</MessageText>
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              ) : (
                <div className={classes.textContentItemMedia}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <span className={classes.timestampMedia}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              )
            ) : (
              <div className={classes.textContentItem}>
                {message.quotedMsg && renderQuotedMessage(message)}
                <MessageText>{message.body}</MessageText>
                <span className={classes.timestamp}>
                  {format(parseISO(message.createdAt), "HH:mm")}
                </span>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment key={message.id}>
          {renderDailyTimestamps(message, index)}
          {renderMessageDivider(message, index)}
          <div className={classes.messageRight} id={`msg-${message.id}`}>
            <IconButton
              variant="contained"
              size="small"
              id="messageActionsButton"
              disabled={message.isDeleted}
              className={classes.messageActionsButton}
              onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
            >
              <ExpandMore />
            </IconButton>
            {shouldShowAgentName(message) && (
              <span className={classes.agentName}>{message.user.name}</span>
            )}
            {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
              //|| message.mediaType === "multi_vcard"
            ) && checkMessageMedia(message)}
            {MEDIA_WITH_OWN_LAYOUT.includes(message.mediaType) && message.mediaUrl ? (
              message.body && ["image", "video", "document", "application"].includes(message.mediaType) ? (
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MessageText>{message.body}</MessageText>
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              ) : (
                <div className={classes.textContentItemMedia}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <span className={classes.timestampMedia}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              )
            ) : (
              <div
                className={clsx(classes.textContentItem, {
                  [classes.textContentItemDeleted]: message.isDeleted,
                })}
              >
                {message.isDeleted && (
                  <Block
                    color="disabled"
                    fontSize="small"
                    className={classes.deletedIcon}
                  />
                )}
                {message.quotedMsg && renderQuotedMessage(message)}
                <MessageText>{message.body}</MessageText>
                <span className={classes.timestamp}>
                  {format(parseISO(message.createdAt), "HH:mm")}
                  {renderMessageAck(message)}
                </span>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    }
  };

  const renderMessages = () => {
    if (messagesList.length === 0 && !loading) {
      return null;
    }
    const items = virtualizer.getVirtualItems();
    return (
      <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative", width: "100%" }}>
        {items.map((virtualItem) => (
          <div
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${virtualItem.start}px)`, display: "flex", flexDirection: "column" }}
          >
            {renderSingleMessage(messagesList[virtualItem.index], virtualItem.index)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        ref={parentRef}
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {renderMessages()}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;